import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { URLS } from 'constants/urls'
import { ArchiveIcon, PlusIcon } from 'ui/icons'
import { Typography, Button } from '@frontend/design_system'
import { Table } from 'ui/molecules/Table'
import { MENU_NAMES } from 'texts/menuNames'
import { profileApi, ProfileApiTypes } from 'api'
import { ActionItems } from 'ui/atoms/ActionItems'
import { useAppDispatch } from 'redux/hooks'
import { Container } from 'ui/molecules/Container'
import { Confirmation } from 'ui/molecules/Confirmation'

import {
  INDICATOR_STATUSES,
  PROFILE_LIST,
  PROFILE_LIST_CONFIRMATION,
} from 'texts/profileList'
import { Company } from 'api/profile/types'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { useApiRequest } from 'hooks/useApiRequest'
import { deactivateProfileSubtitle } from 'texts/profileConfirmModal'
import styles from './styles.module.scss'
import { joinClasses } from 'utils/joinClasses'
import { useFilterSettings } from 'hooks/useFilterSettings'
import { PROFILE_LIST_FILTER_KEY } from 'constants/filters'
import { sortingSelector } from 'redux/sorting/selectors'
import { useSelector } from 'react-redux'
import { resetSelectedFilters, setFilterItems } from 'redux/filters/slice'
import { prepareFiltersForReq, prepareSortingForReq } from 'utils/table'
import {
  filterItemsSelector,
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'
import { useNavigationItems } from 'ui/hooks/useNavigationItems'
import { resetSectionStatuses } from 'redux/login/slice'
import { TableHeadProps } from 'ui/molecules/Table/TableHead/types'
import { prepareFilterItems } from 'utils/filters'
import { ResetFiltersButton } from 'ui/components/ResetFiltersButton'
import { Pagination } from 'ui/molecules/Pagination'
import { FavoriteButton } from 'ui/molecules/FavoriteButton'
import { TableCellLink } from 'ui/atoms/TableCellLink'
import {
  COMPANY_ID_INDEX,
  COMPANY_NAME_INDEX,
  COMPLETED,
  COUNTRY_INDEX,
  headItems,
  IN_PROGRESS,
  INDICATOR_INDEX,
  INDICATOR_STYLES,
  NO_PROFILE,
  STATUS_INDEX,
} from './constants'
import { ProfileListContentProps } from './types'
import { usePagination } from 'hooks/usePagination'
import { TableTooltip } from 'ui/molecules/TableTooltip'

export const ProfileListContent = ({
  isFavorites = false,
}: ProfileListContentProps) => {
  const [companyList, setCompanyList] =
    useState<ProfileApiTypes.GetCompanyListResponse | null>(null)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<Company>()
  const sortingData = useSelector(sortingSelector)
  const selectedFiltersCount = useSelector(selectedFiltersCountSelector)
  const selectedFilters = useSelector(selectedFiltersSelector)
  const filterItems = useSelector(filterItemsSelector)
  const { page, pageSize, handleResetPagination } = usePagination()
  const getFilterSettings = useFilterSettings(
    PROFILE_LIST_FILTER_KEY,
    handleResetPagination
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const navigationItems = useNavigationItems()
  const { getCompanyInfo } = useCompanyInfo(false)
  const { apiRequest } = useApiRequest(
    (id) => profileApi.closeCompany(id),
    undefined,
    true
  )

  const getCompanyListRequest = useApiRequest(() =>
    profileApi.getCompanyList(
      page - 1,
      Number(pageSize),
      false,
      isFavorites ? true : '',
      prepareFiltersForReq(
        selectedFilters.profileList,
        selectedFiltersCount.profileList,
        PROFILE_LIST_FILTER_KEY
      ),
      prepareSortingForReq(sortingData.profileList, PROFILE_LIST_FILTER_KEY)
    )
  )

  const getProfileFiltersRequest = useApiRequest(() =>
    profileApi.getProfileListFilters(false, isFavorites)
  )

  const fetchProfileList = async () => {
    const companyListResponse = await getCompanyListRequest.apiRequest()
    if (companyListResponse) {
      setCompanyList(companyListResponse.data)
    }
  }

  useEffect(() => {
    fetchProfileList()
  }, [page, pageSize, sortingData, selectedFiltersCount.profileList])

  const fetchFilters = async () => {
    const profileListFilters = await getProfileFiltersRequest.apiRequest()

    if (profileListFilters) {
      dispatch(
        setFilterItems({
          items: prepareFilterItems(profileListFilters.data),
          key: PROFILE_LIST_FILTER_KEY,
        })
      )
    }
  }

  useEffect(() => {
    dispatch(resetSectionStatuses())
  }, [])

  useEffect(() => {
    !selectedFiltersCount.profileList && fetchFilters()
  }, [selectedFiltersCount.profileList])

  const handleArchiveCompany = async (company: Company) => {
    setIsConfirmationModalOpen(true)
    setSelectedCompany(company)
  }

  const handleConfirm = async (id: number) => {
    const response = await apiRequest(id)
    if (!response) {
      setIsConfirmationModalOpen(false)
    } else {
      dispatch(resetSelectedFilters())
      fetchProfileList()
      return true
    }
  }

  const handleAddProfile = () => {
    navigate('add')
  }

  const handleModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  const handleRedirectToArchive = () => {
    navigate(`${URLS.PROFILE}${URLS.ARCHIVE}`)
  }

  const getProfilePath = (id: number) =>
    `${URLS.PROFILE}${URLS.PROFILE_LIST}/${id}/edit`

  const handleRowClick = (id: number) => {
    navigate(getProfilePath(id))
  }

  const handleActionItemClick = (url: string) => {
    navigate(url)
  }

  const renderCarListIndicator = (status: Company['isAvailableOnCarList']) => {
    let statusValue
    if (status === true) {
      statusValue = COMPLETED
    } else if (status === false) {
      statusValue = IN_PROGRESS
    } else {
      statusValue = NO_PROFILE
    }

    return (
      <Typography name="Subtitle7" className={styles['indicator-container']}>
        <div
          className={joinClasses(
            styles.indicator,
            styles[INDICATOR_STYLES[statusValue]]
          )}
        />
        {INDICATOR_STATUSES[statusValue]}
      </Typography>
    )
  }

  const formattedHeadItems = useMemo(() => {
    const newHeadItems: TableHeadProps['item'][] = headItems.map((header) => ({
      value: header,
    }))
    newHeadItems[COMPANY_ID_INDEX].filterSettings =
      getFilterSettings('companyIds')
    newHeadItems[COMPANY_NAME_INDEX].filterSettings =
      getFilterSettings('companyNames')
    newHeadItems[COUNTRY_INDEX].filterSettings = getFilterSettings('countries')
    newHeadItems[STATUS_INDEX].filterSettings = getFilterSettings('statuses')
    newHeadItems[INDICATOR_INDEX].filterSettings = getFilterSettings(
      'isAvailableOnCarList'
    )
    return newHeadItems
  }, [filterItems.profileList, getFilterSettings])

  const formattedBodyItems = useMemo(
    () =>
      companyList?.companies.pageItems?.map((company) => ({
        items: [
          <TableCellLink to={getProfilePath(company.id)}>
            {company.id}
          </TableCellLink>,
          <TableCellLink to={getProfilePath(company.id)}>
            <TableTooltip title={company.name} tooltipThreshold={30} />
          </TableCellLink>,
          company.country,
          <Typography
            name="caption1WBold"
            className={joinClasses(
              styles.status,
              styles[company.accountStatus]
            )}
          >
            {company.accountStatus}
          </Typography>,
          renderCarListIndicator(company.isAvailableOnCarList),
          <div className={styles['action-items']}>
            <ActionItems
              items={navigationItems.map(({ label, getPath, isDisabled }) => ({
                value: label,
                path: getPath(String(company.id)),
                onClick: handleActionItemClick,
                isDisabled,
              }))}
              listItemWidth="200px"
              waitPreparation={() => getCompanyInfo(company.id)}
            />
            <FavoriteButton
              isFavorite={company.isFavorite}
              companyId={String(company.id)}
              companyName={company.name}
              onFavoriteChange={fetchProfileList}
              variant="secondary"
            />
            <ArchiveIcon
              color="blue700"
              className={styles['archive-icon']}
              dataTestId="archive-icon"
              onClick={(e) => {
                e.stopPropagation()
                handleArchiveCompany(company)
              }}
            />
          </div>,
        ],
        onClick: () => handleRowClick(company.id),
      })),
    [selectedCompany, companyList, navigationItems]
  )

  return (
    <div>
      {isConfirmationModalOpen && selectedCompany && (
        <Confirmation
          confirmModalProps={{
            question: `${PROFILE_LIST_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${selectedCompany?.name}?`,
            onConfirm: () => handleConfirm(selectedCompany.id),
            onClose: handleModalClose,
            title: `${PROFILE_LIST.ARCHIVE} ${selectedCompany.name}?`,
          }}
          successModalProps={{
            onClose: handleModalClose,
            title: PROFILE_LIST_CONFIRMATION.SUCCESS_MODAL.TITLE,
            buttonLabel: PROFILE_LIST_CONFIRMATION.SUCCESS_MODAL.BUTTON_LABEL,
            onConfirm: handleRedirectToArchive,
            subTitle: deactivateProfileSubtitle(selectedCompany?.name),
          }}
        />
      )}
      <Container
        title={
          isFavorites ? MENU_NAMES.FAVORITES : MENU_NAMES.PROFILE_LIST.NAME
        }
        tooltip={!isFavorites ? PROFILE_LIST.TOOLTIP_CONTENT : undefined}
        withCompanyName={false}
        loading={
          getCompanyListRequest.loading && !getCompanyListRequest.isLoaded
        }
      >
        <div className={styles.list}>
          <div className={styles.header}>
            <ResetFiltersButton
              selectedFiltersCount={selectedFiltersCount.profileList}
            />
            {!isFavorites && (
              <div className={styles.button}>
                <Button
                  size="small"
                  typographyName="Button2"
                  variant="secondary-border"
                  onClick={handleAddProfile}
                  label={PROFILE_LIST.BUTTON_LABEL}
                  iconPosition="right"
                  dataTestId="add-profile-button"
                  icon={<PlusIcon color="blue700" />}
                />
              </div>
            )}
          </div>
          <div className={styles.table}>
            {formattedBodyItems && (
              <Table
                headItems={formattedHeadItems}
                bodyItems={formattedBodyItems}
                initItemsWidth={['80', '228', '140', '129', '120', '132']}
                dataTestId="profile-list-table"
              />
            )}
          </div>
        </div>
        <footer className={styles.pagination}>
          <Pagination
            defaultPageSize={Number(pageSize)}
            pageItemCount={companyList?.companies.pageItems.length}
            total={companyList?.companies?.totalItems || 0}
            pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
          />
        </footer>
      </Container>
    </div>
  )
}
